
import React from "react";
import { Container } from "@/components/ui/container";
import { Shield, Lock, Users, Eye, Database, Settings, Bell, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const Privacy = () => {
  const sections = [
    {
      id: "intro",
      icon: Shield,
      title: "Privacy Overview",
      description: "Our commitment to protecting your personal information and privacy rights."
    },
    {
      id: "collection",
      icon: Database,
      title: "Data Collection",
      description: "Information we collect and how we gather it responsibly."
    },
    {
      id: "usage",
      icon: Settings,
      title: "Data Usage",
      description: "How we use your information to enhance your TeenVisor experience."
    },
    {
      id: "sharing",
      icon: Users,
      title: "Information Sharing",
      description: "Circumstances under which we may share your data with third parties."
    },
    {
      id: "security",
      icon: Lock,
      title: "Data Security",
      description: "Security measures we implement to protect your personal information."
    },
    {
      id: "rights",
      icon: Eye,
      title: "Your Rights",
      description: "Your rights regarding your personal data and how to exercise them."
    },
    {
      id: "children",
      icon: Bell,
      title: "Minor Protection",
      description: "Special privacy protections for users under 18 years of age."
    },
    {
      id: "contact",
      icon: Globe,
      title: "Privacy Contact",
      description: "How to reach our privacy team with questions or concerns."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      
      {/* Hero Section */}
      <div className="relative py-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse-gentle"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse-gentle"></div>
        </div>
        
        <Container className="relative">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6 animate-float">
            <Shield className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent leading-tight">
            Privacy Policy
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Your privacy is fundamental to our mission. Learn how we protect, use, and respect your personal information.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Effective Date: May 31, 2025
          </div>
        </Container>
      </div>

      {/* Quick Navigation */}
      <Container className="py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-12">
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="group flex flex-col items-center p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 hover:bg-card/80 transition-all duration-300 hover:scale-105"
            >
              <section.icon className="h-6 w-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-xs font-medium text-center group-hover:text-primary transition-colors leading-tight">
                {section.title}
              </span>
            </a>
          ))}
        </div>
      </Container>

      <main className="flex-grow pb-20">
        <Container>
          <div className="max-w-4xl mx-auto space-y-16">
            
            {/* Introduction Section */}
            <Card id="intro" className="border-0 shadow-lg bg-gradient-to-r from-primary/5 to-secondary/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Privacy Policy Overview</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      At TeenVisor Inc. ("Company," "we," "us," or "our"), we recognize that privacy is a fundamental right. This Privacy Policy describes how we collect, use, store, and protect your personal information when you use our platform and services. We are committed to maintaining the highest standards of data protection and transparency.
                    </p>
                  </div>
                </div>
                
                <div className="bg-background/50 p-6 rounded-lg border border-border/50">
                  <p className="font-semibold mb-4 text-lg">Our Privacy Commitments:</p>
                  <ul className="space-y-3 text-muted-foreground leading-relaxed">
                    <li>• We collect only the information necessary to provide and improve our services</li>
                    <li>• Your personal data is never sold to third parties for commercial purposes</li>
                    <li>• You maintain full control over your privacy settings and data sharing preferences</li>
                    <li>• We implement industry-standard security measures to protect your information</li>
                    <li>• Special protections are in place for users under 18 years of age</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Information Collection */}
            <Card id="collection" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-500/10 rounded-lg">
                    <Database className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Information We Collect</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      We collect information in several ways to provide you with personalized experiences and connect you with relevant opportunities on our platform.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      Information You Provide
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Account registration details (name, email address, date of birth)</li>
                      <li>• Profile information (interests, skills, educational background, career goals)</li>
                      <li>• Communication data (messages, forum posts, feedback submissions)</li>
                      <li>• Application materials (resumes, portfolios, project submissions)</li>
                      <li>• Optional demographic information for diversity and inclusion initiatives</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Eye className="h-5 w-5 text-secondary" />
                      Information Collected Automatically
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Usage analytics (pages visited, features used, time spent on platform)</li>
                      <li>• Device information (browser type, operating system, IP address)</li>
                      <li>• Performance data (load times, error reports, crash logs)</li>
                      <li>• Cookies and similar tracking technologies for functionality and analytics</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Globe className="h-5 w-5 text-green-500" />
                      Information from Third Parties
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Social media profile information (when you choose to connect accounts)</li>
                      <li>• Educational institution data (with proper authorization)</li>
                      <li>• Partner organization information (for program participants)</li>
                      <li>• Public information from professional networking sites</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Usage */}
            <Card id="usage" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-green-500/10 rounded-lg">
                    <Settings className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">How We Use Your Information</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      We use your information to provide, maintain, and improve our services while respecting your privacy preferences and applicable laws.
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="p-6 bg-primary/5 rounded-lg">
                      <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                        🤝 Platform Services
                      </h4>
                      <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                        <li>• Facilitate mentor-mentee connections</li>
                        <li>• Provide personalized recommendations</li>
                        <li>• Enable community interactions</li>
                        <li>• Process opportunity applications</li>
                      </ul>
                    </div>
                    
                    <div className="p-6 bg-secondary/5 rounded-lg">
                      <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                        📊 Analytics & Improvement
                      </h4>
                      <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                        <li>• Analyze platform usage patterns</li>
                        <li>• Improve user experience and features</li>
                        <li>• Conduct research on educational outcomes</li>
                        <li>• Optimize matching algorithms</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="p-6 bg-green-500/5 rounded-lg">
                      <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                        🛡️ Safety & Security
                      </h4>
                      <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                        <li>• Monitor for inappropriate behavior</li>
                        <li>• Prevent fraud and abuse</li>
                        <li>• Verify user identities when necessary</li>
                        <li>• Maintain platform security</li>
                      </ul>
                    </div>
                    
                    <div className="p-6 bg-purple-500/5 rounded-lg">
                      <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                        📧 Communication
                      </h4>
                      <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                        <li>• Send service-related notifications</li>
                        <li>• Provide customer support</li>
                        <li>• Share relevant opportunities</li>
                        <li>• Deliver educational content</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Information Sharing */}
            <Card id="sharing" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-orange-500/10 rounded-lg">
                    <Users className="h-6 w-6 text-orange-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Information Sharing Practices</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      We limit information sharing to specific circumstances that serve legitimate purposes and protect user interests.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      When We Share Information
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• <strong>With your explicit consent:</strong> When you authorize specific data sharing</li>
                      <li>• <strong>Service providers:</strong> Trusted partners who help operate our platform (with strict confidentiality agreements)</li>
                      <li>• <strong>Legal requirements:</strong> When required by law, court order, or government request</li>
                      <li>• <strong>Safety purposes:</strong> To protect users from harm or prevent illegal activities</li>
                      <li>• <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets (with user notification)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Lock className="h-5 w-5 text-primary" />
                      What We Never Share
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Personal information for commercial advertising purposes</li>
                      <li>• Private messages or communications without consent</li>
                      <li>• Financial information or payment details</li>
                      <li>• Information that could identify specific individuals in research</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card id="security" className="border-0 shadow-lg bg-gradient-to-r from-blue-500/5 to-purple-500/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-500/10 rounded-lg">
                    <Lock className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Data Security Measures</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      We implement comprehensive security measures to protect your personal information from unauthorized access, use, or disclosure.
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                        🔒 Technical Safeguards
                      </h4>
                      <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                        <li>• End-to-end encryption for sensitive data</li>
                        <li>• Secure Socket Layer (SSL) technology</li>
                        <li>• Regular security audits and penetration testing</li>
                        <li>• Multi-factor authentication options</li>
                        <li>• Automated threat detection systems</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                        👥 Administrative Controls
                      </h4>
                      <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                        <li>• Limited access on a need-to-know basis</li>
                        <li>• Regular employee security training</li>
                        <li>• Confidentiality agreements for all staff</li>
                        <li>• Incident response procedures</li>
                        <li>• Regular data backup and recovery testing</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-6 bg-background/50 rounded-lg border border-border/50">
                  <p className="font-semibold mb-3">Security Incident Reporting</p>
                  <p className="text-muted-foreground leading-relaxed">
                    In the unlikely event of a data breach, we will notify affected users within 72 hours and provide detailed information about the incident, steps taken to address it, and recommendations for protecting your information.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Privacy Rights */}
            <Card id="rights" className="border-0 shadow-lg bg-gradient-to-r from-green-500/5 to-blue-500/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-green-500/10 rounded-lg">
                    <Eye className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Your Privacy Rights</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      You have comprehensive rights regarding your personal data. We provide easy-to-use tools and processes to exercise these rights.
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mt-1">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-lg">Access Your Data</h4>
                        <p className="text-muted-foreground leading-relaxed">Request a comprehensive report of all personal information we have collected about you, including how it's used and shared.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-secondary/10 rounded-full flex items-center justify-center mt-1">
                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-lg">Correct Information</h4>
                        <p className="text-muted-foreground leading-relaxed">Update, modify, or correct any inaccurate or incomplete personal information in your profile.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-green-500/10 rounded-full flex items-center justify-center mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-lg">Data Portability</h4>
                        <p className="text-muted-foreground leading-relaxed">Download your data in a structured, commonly-used format to transfer to another service.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-red-500/10 rounded-full flex items-center justify-center mt-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-lg">Delete Your Data</h4>
                        <p className="text-muted-foreground leading-relaxed">Request permanent deletion of your account and associated personal data, subject to legal retention requirements.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-purple-500/10 rounded-full flex items-center justify-center mt-1">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-lg">Control Processing</h4>
                        <p className="text-muted-foreground leading-relaxed">Object to or restrict certain types of data processing, including marketing communications and automated decision-making.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-orange-500/10 rounded-full flex items-center justify-center mt-1">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 text-lg">Manage Sharing</h4>
                        <p className="text-muted-foreground leading-relaxed">Control who can see your profile information and how your data is used for matching and recommendations.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-6 bg-background/50 rounded-lg border border-border/50">
                  <p className="font-semibold mb-3">Exercising Your Rights</p>
                  <p className="text-muted-foreground leading-relaxed">
                    To exercise any of these rights, contact our privacy team at privacy@teenvisor.com or use the privacy controls in your account settings. We will respond to your request within 30 days and may require identity verification for security purposes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Minor Protection */}
            <Card id="children" className="border-0 shadow-lg bg-gradient-to-r from-purple-500/5 to-pink-500/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-500/10 rounded-lg">
                    <Bell className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Protection of Minors</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      We implement enhanced privacy protections for users under 18 years of age, in compliance with applicable laws including COPPA and GDPR.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      Enhanced Privacy Measures
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Parental consent required for users under 13 years of age</li>
                      <li>• Restricted data collection limited to essential platform functionality</li>
                      <li>• Enhanced content moderation and safety monitoring</li>
                      <li>• Limited information sharing with additional oversight</li>
                      <li>• Age-appropriate privacy settings and controls</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      Parental Rights and Controls
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Parents can review their child's personal information</li>
                      <li>• Right to request deletion of their child's account and data</li>
                      <li>• Ability to refuse further collection of their child's information</li>
                      <li>• Access to privacy settings and activity monitoring tools</li>
                      <li>• Direct communication channel with our privacy team</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-8 p-6 bg-background/50 rounded-lg border border-border/50">
                  <p className="font-semibold mb-3">Additional Safety Features for Teens</p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                      <li>• Verified mentor program with background checks</li>
                      <li>• Moderated communication channels</li>
                      <li>• Restricted contact information sharing</li>
                      <li>• Educational resources on digital privacy</li>
                    </ul>
                    <ul className="space-y-2 text-muted-foreground leading-relaxed text-sm">
                      <li>• Easy reporting mechanisms for inappropriate behavior</li>
                      <li>• Regular safety reminders and tips</li>
                      <li>• Age-appropriate content filtering</li>
                      <li>• Prompt response to safety concerns</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Section */}
            <Card id="contact" className="border-0 shadow-lg bg-gradient-to-r from-primary/10 to-secondary/10">
              <CardContent className="p-8 text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6 mx-auto">
                  <Globe className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-6 leading-tight">Privacy Team Contact</h2>
                <p className="text-muted-foreground mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                  Our dedicated privacy team is here to help with any questions, concerns, or requests regarding your personal information and privacy rights.
                </p>
                
                <div className="bg-background/50 p-6 rounded-lg border border-border/50 mb-8 max-w-2xl mx-auto">
                  <h3 className="font-semibold mb-4 text-lg">Privacy Office</h3>
                  <div className="space-y-2 text-muted-foreground">
                    <p>TeenVisor Inc. - Privacy Team</p>
                    <p>Email: teenvisor@gmail.com</p>
                    <p>Data Protection Officer: teenvisor@gmail.com</p>
                    <p>Emergency Privacy Concerns: +91 9994342931</p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a 
                    href="mailto:teenvisor@gmail.com?subject=Privacy Policy Inquiry" 
                    className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    Contact Privacy Team
                  </a>
                  <a 
                    href="mailto:teenvisor@gmail.com?subject=Data Protection Request" 
                    className="flex items-center gap-2 px-6 py-3 border border-border rounded-lg hover:bg-accent transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    Data Protection Officer
                  </a>
                </div>
                
                <div className="mt-8 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                  <p className="text-sm text-muted-foreground">
                    <strong>Response Time:</strong> We respond to all privacy inquiries within 72 hours and resolve requests within 30 days as required by applicable privacy laws.
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </Container>
      </main>
      
    </div>
  );
};

export default Privacy;
